/*
 * Copyright 2025 Intuition Robotics ltd
 */
import {COLORS} from "@res/colors";
import {AlertRow} from "./AlertRow";
import * as React from "react";
import {BaseComponent, BrowserHistoryModule} from "@intuitionrobotics/thunderstorm/frontend";
import {AlertsModule, OnAlertsUpdated} from "@modules/AlertsModule";
import {AlertFeedback, AlertStatus, AlertType, CommentMap, DB_Alert, DB_Comment, FeedbackCategory} from "@app/app-shared/alert";
import {AnalyticConsts, AnalyticsModule} from "@modules/AnalyticsModule";
import {_keys, auditBy, DB_Object, PartialProperties, sortArray} from "@intuitionrobotics/ts-common";
import {NoData} from "../graphs/NoData";
import {OnTimeFilterChanged, PARAM_ALERT} from "@modules/UserPreferencesModule";
import {Card, CARD_BORDER_RADIUS} from "../../widget/Card";
import {CardHeader} from "../../widget/CardHeader";
import {FONTS} from "@res/fonts";
import * as emotion from "emotion";
import {VGap} from "../../widget/VGap";
import {ErrorToast} from "../../../themes/toasts";
import {ICONS} from "@res/icons";
import {UsersModule} from "@modules/UsersModule";
import {AddComment} from "./AddComment";
import {CommentsModule, OnCommentDeleted, OnCommentUpdated} from "@modules/CommentsModule";
import {CommentRow} from "./CommentRow";
import {MessagingModule} from "@modules/MessagingModule";
import {GroupType} from "@app/ir-q-app-common/types/contacts";
import {DB_Patient} from "@app/app-shared/patient";
import {ChatsContext, ChatsMap} from "../messages/ChatsContext";
import {CCInbox} from "@app/ir-q-app-common/types/message-api";
import {ContactsContext, ContactsMap} from "../messages/ContactsContext";
import {PatientsContext} from "../patients/PatientsContext";

const BodyCSS = emotion.css(
    {
        flex: 1,
        overflow: "auto"
    }
);

const cardHeader = emotion.css(
    {
        height: 50,
        padding: 0
    }
);

const commentDivider = emotion.css(
    {
        width: "95%",
        height: "1px",
        backgroundColor: COLORS.grays_2,
        margin: "10px"
    }
);

type State = {
    details?: string
}

type Props = {
    patient: DB_Patient
    demo: boolean
};

export class AlertPanel
    extends BaseComponent<Props, State>
    implements OnAlertsUpdated, OnTimeFilterChanged, OnCommentUpdated, OnCommentDeleted {

    __onAlertsUpdated() {
        this.forceUpdate();
    }

    __onTimeFilterChanged() {
        this.forceUpdate();
    }

    __onCommentUpdated(created: boolean, success: boolean, error?: string) {
        if (!success) {
            ErrorToast(error || "An error occurred");
        }
        this.forceUpdate();
    }

    __onCommentDeleted = () => {
        this.forceUpdate();
    };

    constructor(props: Props) {
        super(props);
        const details = BaseComponent.getQueryParameter(PARAM_ALERT);
        this.state = {
            details: details
        };
    }

    private onFeedbackSubmit = (contacts: ContactsMap, alert: DB_Alert<any>, feedback: AlertFeedback, chats: ChatsMap) => {
        if (!feedback || !_keys(feedback).some(key => feedback && feedback[key])) {
            this.logError("Attempted to submit empty feedback.");
            ErrorToast("You must provide feedback for the notification.");
            return;
        }

        if (alert.status === AlertStatus.REJECTED) {
            this.logError("Should not submit feedback for a rejected alert.");
            ErrorToast("An error occurred.");
            return;
        }

        if (this.props.demo) {
            const user = UsersModule.getCurrentUser();
            if (user)
                alert._updated = auditBy(user);
            return AlertsModule.demoMarkResolved(alert);
        }

        AlertsModule.markAsResolved(alert, feedback)

        if (alert.type === AlertType.PatientContactRequest && alert.messageTrigger) {
            const chat = chats[alert.messageTrigger.conversationId]
            if (chat)
                this.markAsRead(contacts, chat, alert.category)
        }
        AnalyticsModule.logCareManagerEvent({
            objectType: AnalyticConsts.ObjectTypes.CUSTOMERS,
            entity: AnalyticConsts.Entities.USER,
            activityStatus: AnalyticConsts.ActivityStatus.COMPLETE,
            objectSubType: AnalyticConsts.ObjectSubTypes.DASHBOARD,
            activityDetails: AnalyticConsts.ActivityDetails.NOTIFICATION_CHANGE,
            objectId: alert._id
        });

    };

    private markAsRead(contacts: ContactsMap, chat: CCInbox, category: GroupType) {
        const chatContacts = chat.contactIds.map(cId => contacts[cId]);
        const groupContact = chatContacts.find(contact => contact.contactType === "group" && contact.contactData.type === category);
        if (!groupContact)
            return;

        MessagingModule.setAsRead(chat.lastMessage._id, groupContact._id).then(() => chat.unreadCount = 0).catch(console.error);
    }

    // All the alerts get the same feedback options for now.
    private renderAlerts = (patientAlerts: DB_Alert<any>[], feedbackCategories: FeedbackCategory[]) => {
        if (patientAlerts.length === 0)
            return <NoData/>;

        const sortedAlerts = sortArray(patientAlerts, alert => alert._created.auditAt.timestamp);
        return sortedAlerts.map((alert) => {
            return this.renderAlertRow(feedbackCategories, alert, () => {
                this.setState({details: alert._id})
                BrowserHistoryModule.addQueryParam(PARAM_ALERT, alert._id);
            });
        });

    };

    private renderAlertDetails = (alert: DB_Alert<any>, feedbackCategories: FeedbackCategory[]) => {
        const comments: DB_Comment<keyof CommentMap>[] = CommentsModule.getCommentsByAlert(alert._id);
        const sortedComments = sortArray(comments, (c: DB_Comment<keyof CommentMap>) => c.timestamp.auditAt.timestamp);
        return <div className={"match_width"}>
            {this.renderAlertRow(feedbackCategories, alert)}
            <div className={commentDivider}/>
            <PatientsContext.Consumer>
                {({demo}) => {
                    return <AddComment
                        alert={alert}
                        saveEnabled={!demo}
                        onSubmit={(comment: string) => {
                            const user = UsersModule.getCurrentUserByEmail();
                            const isAddingCommentToAlert = CommentsModule.isAddingCommentToAlert(alert._id);
                            if (isAddingCommentToAlert)
                                return;

                            const newComment: PartialProperties<DB_Comment<"text">, keyof DB_Object> = {
                                type: "text",
                                data: comment,
                                timestamp: auditBy(user?.mail || "fe-stub"),
                                alertId: alert._id
                            };
                            CommentsModule.create(newComment);
                        }} onCancel={() => this.setState({details: undefined})}/>

                }}
            </PatientsContext.Consumer>
            <div className={commentDivider}/>
            {/*{this.renderFeedbackAsComment(alert)}*/}
            {
                sortedComments && sortedComments.map(
                    (_comment: DB_Comment<keyof CommentMap>) => <CommentRow key={_comment._id} comment={_comment}
                                                                            organizationId={this.props.patient.organizationId}
                    />
                )
            }
        </div>;
    };

    private renderAlertRow = (feedbackCategories: FeedbackCategory[], alert: DB_Alert<any>, onDetailsOpen?: () => void) =>
        <ContactsContext.Consumer>
            {({contacts}) => {
                return <ChatsContext.Consumer>
                    {({chats}) => <AlertRow
                        alert={alert}
                        key={alert._id}
                        onFeedbackSubmit={feedback => this.onFeedbackSubmit(contacts, alert, feedback, chats || {})}
                        feedbackCategories={{feedback: feedbackCategories}}
                        onDetailsOpen={onDetailsOpen}/>
                    }
                </ChatsContext.Consumer>
            }}
        </ContactsContext.Consumer>;

    renderMainCardHeader = () => {
        return <>
            <div style={{width: 24, height: 24, marginLeft: 23, marginRight: 9}}>
                {ICONS.icon_bell_s(COLORS.white, 24)}
            </div>
            <div>
                {FONTS.medium_18_white("Notifications")}
            </div>
        </>;
    };

    renderCardDetailsHeader = () => {
        return <>
            <div className={"clickable"} style={{width: 24, height: 24, marginLeft: 23, marginRight: 9}}
                 onClick={() => {
                     this.setState({details: undefined})
                     BrowserHistoryModule.removeQueryParam(PARAM_ALERT);
                 }}>
                {FONTS.medium_18("<", {color: COLORS.white})}
            </div>
            <div>
                {FONTS.reg_14(`All ${this.props.patient.name}'s Notifications`, {color: COLORS.white})}
            </div>
        </>;
    };


    render() {
        // We should get the time filter here so its clear that when we change time filter
        // we need to dispatch an event and catch it in this component
        const patientAlerts = AlertsModule.getPatientAlerts(this.props.patient._id);
        patientAlerts && AlertsModule.sortAlertsByFeedback(patientAlerts);

        return <Card>
            <CardHeader className={cardHeader}
                        style={{
                            backgroundColor: COLORS.macaroni_and_cheese,
                            justifyContent: "normal"
                        }}>
                {!!this.state.details ? this.renderCardDetailsHeader() : this.renderMainCardHeader()}
            </CardHeader>
            <div className={"ll_v_s"} style={{
                ...patientAlerts && patientAlerts.length && {height: 5.5 * 73},
                overflow: "hidden",
                // // Review this:
                // maxWidth: 500,
                borderRadius: `0px 0px ${CARD_BORDER_RADIUS} ${CARD_BORDER_RADIUS}`
            }}>
                <div className={BodyCSS}>
                    {this.renderCardContent(patientAlerts)}
                </div>
            </div>
        </Card>;
    }

    private renderCardContent = (patientAlerts?: DB_Alert<any>[]) => {
        if (patientAlerts === undefined)
            return <VGap v={300}/>;

        const feedbackCategories = AlertsModule.getFeedbackCategories();
        const alert = patientAlerts.find(_alert => _alert._id === this.state.details);
        if (alert)
            return this.renderAlertDetails(alert, feedbackCategories);

        return this.renderAlerts(patientAlerts, feedbackCategories);
    };

// @ts-ignore
    // private renderFeedbackAsComment = (alert: DB_Alert) => {
    // 	const feedback = alert.feedback;
    // 	if(!feedback || !alert.lastUpdated)
    // 		return
    //
    // 	// @ts-ignore
    // 	return <CommentRow key={generateHex(8)} comment={{
    // 		text: 'Feedback provided: ' + "\n" + _values(feedback).map(f => {
    // 			return f?.join("\n") || ""
    // 		}),
    // 		timestamp: alert.lastUpdated
    // 	}}/>
    // };
}
